import { useQuery } from '@tanstack/react-query';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';
import type { components as SensorGraphComponents } from '@/api/types/sensor-graph-service';

// S5-scoped query layer. The synthetic fixture in `./fixtures` remains
// the in-memory source backing the MSW handlers; these hooks let S5
// consume the *real* OpenAPI wire shapes through openapi-fetch /
// apiFetch, so swapping in the live backend later is a single-file
// change (drop the MSW handlers, point baseUrl at the real gateway).
//
// We use untyped apiFetch instead of the typed `clients.sensorGraph`
// client because some endpoints have RFC3339Nano dates as strings and
// the typed client's strict params would require a date-time string
// for `from`/`to` here too — easy to satisfy, but apiFetch keeps the
// hook surface minimal for the first migration step. Subsequent
// hardening upgrades to the typed client.

type Scalars = {
  assetId: string;
  sensorId: string;
  sensorName: string;
  series: Array<{
    metric: 'RMS' | 'Peak' | 'Crest' | 'Kurtosis';
    unit: string;
    points: Array<{ ts: string; value: number }>;
    last: {
      value: number;
      tone: 'critical' | 'warn' | 'watch' | 'healthy' | 'info';
    };
  }>;
};

export function useScalarTrends(assetId: string) {
  const config = useApiConfig();
  return useQuery({
    queryKey: ['scalar-trends', assetId],
    queryFn: async (): Promise<Scalars> => {
      const res = await apiFetch(
        config,
        `/v1/telemetry/assets/${assetId}/scalar-trends`,
      );
      if (!res.ok) throw new Error(`scalar-trends failed (${res.status})`);
      return res.json();
    },
    staleTime: 60_000,
  });
}

type FftThumbnails =
  SensorGraphComponents['schemas']['FftThumbnailsResponse'];

export function useFftThumbnails(sensorId: string, range: { from: string; to: string }) {
  const config = useApiConfig();
  return useQuery({
    queryKey: ['fft-thumbnails', sensorId, range.from, range.to],
    queryFn: async (): Promise<FftThumbnails> => {
      const qs = new URLSearchParams({
        from: range.from,
        to: range.to,
        axis: 'z',
      });
      const res = await apiFetch(
        config,
        `/v1/graphs/sensors/${sensorId}/fft/thumbnails?${qs}`,
      );
      if (!res.ok) throw new Error(`fft-thumbnails failed (${res.status})`);
      return res.json();
    },
    staleTime: 60_000,
  });
}

type Annotations = SensorGraphComponents['schemas']['AnnotationsResponse'];

export function useAnnotations(sensorId: string, range: { from: string; to: string }) {
  const config = useApiConfig();
  return useQuery({
    queryKey: ['annotations', sensorId, range.from, range.to],
    queryFn: async (): Promise<Annotations> => {
      const qs = new URLSearchParams({
        from: range.from,
        to: range.to,
      });
      const res = await apiFetch(
        config,
        `/v1/graphs/sensors/${sensorId}/annotations?${qs}`,
      );
      if (!res.ok) throw new Error(`annotations failed (${res.status})`);
      return res.json();
    },
    staleTime: 60_000,
  });
}

type Spectrogram = SensorGraphComponents['schemas']['SpectrogramResponse'];

export function useSpectrogram(sensorId: string, range: { from: string; to: string }) {
  const config = useApiConfig();
  return useQuery({
    queryKey: ['spectrogram', sensorId, range.from, range.to],
    queryFn: async (): Promise<Spectrogram> => {
      const qs = new URLSearchParams({
        from: range.from,
        to: range.to,
        axis: 'z',
      });
      const res = await apiFetch(
        config,
        `/v1/graphs/sensors/${sensorId}/spectrogram?${qs}`,
      );
      if (!res.ok) throw new Error(`spectrogram failed (${res.status})`);
      return res.json();
    },
    staleTime: 60_000,
  });
}

type Spectrum = SensorGraphComponents['schemas']['SpectrumResponse'];

/**
 * Single-burst FFT magnitude spectrum at the requested moment. The
 * server picks the burst nearest `at` within its tolerance window.
 * When `at` is null (no cursor pinned), the query is disabled and the
 * caller falls back to the inline fixture; pin a moment to fire it.
 */
export function useSpectrum(sensorId: string, at: string | null) {
  const config = useApiConfig();
  return useQuery({
    enabled: at != null,
    queryKey: ['spectrum', sensorId, at],
    queryFn: async (): Promise<Spectrum> => {
      const qs = new URLSearchParams({
        at: at!,
        axis: 'z',
      });
      const res = await apiFetch(
        config,
        `/v1/graphs/sensors/${sensorId}/spectrum?${qs}`,
      );
      if (!res.ok) throw new Error(`spectrum failed (${res.status})`);
      return res.json();
    },
    staleTime: 60_000,
  });
}

type WaveformResponse = {
  captureId: string;
  sensorId: string;
  sampleRateHz: number;
  unit: string;
  capturedAt: string;
  samples: number[];
};

/**
 * Time-domain waveform for a single capture. `captureId` is opaque to
 * the client — typically `cap_<assetId>_<cursor>` produced by S5 from
 * the active asset + cursor moment. Disabled when captureId is null
 * (no cursor pinned yet); caller falls back to fixture during that
 * window.
 */
export function useWaveform(captureId: string | null) {
  const config = useApiConfig();
  return useQuery({
    enabled: captureId != null,
    queryKey: ['waveform', captureId],
    queryFn: async (): Promise<WaveformResponse> => {
      const res = await apiFetch(
        config,
        `/v1/telemetry/captures/${captureId}/waveform`,
      );
      if (!res.ok) throw new Error(`waveform failed (${res.status})`);
      return res.json();
    },
    staleTime: 60_000,
  });
}
